import { QueryExpr } from '@apis/Resources';
import { QuerySelectExpr } from '@apis/Resources/model';
import { ChartTypes } from '@root/Components/Charts/Common';
import { QueryDescriptorService } from '@root/Components/Filter/Services';
import { EventEmitter } from '@root/Services/EventEmitter';
import { FormatService, NamedFormats } from '@root/Services/FormatService';
import { QueryDatasource } from '@root/Services/Query/QueryDatasource';
import { SchemaService, SchemaValueProvider } from '@root/Services/QueryExpr';
import { makeAutoObservable, toJS } from 'mobx';
import { deepObserve } from 'mobx-utils';
import { DatasourceSchemaContext } from '../../Filter/DatasourceContext';
import { DashboardItemModel } from '../Models';
import { ChartConfig, chartInfo } from './ChartRenderer';

interface IChartItemModel {
    getDatasource(datasourceName: string): QueryDatasource | undefined;
    settings: ChartConfig;
    updateSettings(settings: ChartConfig): void;
}

class ChartEditorBase<TChartModel extends IChartItemModel> {
    protected boundSettings: unknown;
    public onClose = EventEmitter.empty();
    public loading = new EventEmitter(true);
    public onSettingsChanged = EventEmitter.empty();
    public settings: ChartConfig;
    public isEditMode = false;
    public hideFilter = false;
    public schemaSvc: SchemaService;

    public readonly numericOps: ReadonlyArray<string> = ['sum', 'avg', 'min', 'max', 'count'];
    public readonly statOps: ReadonlyArray<string> = ['percent'];
    public readonly valueOps: ReadonlyArray<string> = ['countuniquevalues'];
    public readonly allOps: ReadonlyArray<string> = [...this.numericOps, ...this.statOps, ...this.valueOps];

    public readonly queryDescriptorSvc: QueryDescriptorService;
    public readonly valueProvider: SchemaValueProvider;

    public constructor(
        public chartModel: TChartModel,
        public readonly schemaCtx: DatasourceSchemaContext,
        isEditMode = false,
        cloneSettings: boolean = true
    ) {
        this.settings = cloneSettings ? structuredClone(this.chartModel.settings) : this.chartModel.settings;
        this.schemaSvc = schemaCtx.schemaSvc;
        this.queryDescriptorSvc = schemaCtx.queryDescriptorSvc;
        this.valueProvider = schemaCtx.valueProvider;
        this.isEditMode = isEditMode;
    }

    public async init() {
        this.loading.emit(true);
        try {
            const datasource = this.getDatasource();
            if (datasource) {
                await datasource.schema.getSchema();
            }
        } finally {
            this.loading.emit(false);
        }
    }

    public saveChanges() {
        this.chartModel.settings.title = this.settings.title;
        this.chartModel.updateSettings(this.settings);
    }

    public handleSaveRequest = () => {
        this.onSettingsChanged.emit();
        Object.assign(this.boundSettings, this.settings.settings);
    };

    public getDatasource() {
        return this.chartModel.getDatasource(this.chartModel.settings.datasourceName);
    }

    public getDatasourceDescription() {
        const { noun, nounPlural, name } = this.getDatasource() ?? {};
        return { noun: noun ?? 'record', nounPlural: nounPlural ?? 'records', name };
    }

    public setTitle = (title: string) => {
        this.settings.title = title;
        this.settings.title = title;
        this.invalidate();
    };
    public get title() {
        return this.settings.title;
    }

    public setChartType = (type: ChartTypes) => {
        this.settings.chartType = type;
        const typeInfo = chartInfo.get(type);
        this.settings.settings = this.getDefaultSettings(type) ?? {};
        typeInfo?.cleanConfig?.(this.settings as unknown as ChartConfig, this.getDatasource()!);
        this.invalidate();
    };
    public get chartType() {
        return this.settings.chartType;
    }

    public getFilters() {
        return this.settings.filters;
    }

    public setFilters(filters: QueryExpr[]) {
        this.settings.filters = filters;
        this.invalidate();
    }

    public createGroupAccessor<T extends string>(index: number, name: T) {
        return this.createGetterSetter(name, this.settings.groups, index);
    }
    public getGroup(index: number) {
        return this.settings.groups[index];
    }
    public hasGroup(index: number) {
        return !!this.settings.groups[index];
    }
    public setGroup(index: number, expr: QuerySelectExpr) {
        this.settings.groups[index] = expr;
        this.invalidate();
    }
    public removeGroup(index: number) {
        this.settings.groups.splice(index, 1);
        this.invalidate();
    }

    public createValueAccessor<T extends string>(index: number, name: T) {
        return this.createGetterSetter(name, this.settings.values, index);
    }
    public setValue(index: number, expr: QuerySelectExpr) {
        this.settings.values[index] = expr;
        this.invalidate();
    }
    public getValue(index: number) {
        return this.settings.values[index];
    }
    public removeValue(index: number) {
        this.settings.values.splice(index, 1);
        this.invalidate();
    }
    public hasValue(index: number) {
        return !!this.settings.values[index];
    }

    public getDefaultFormatter(expr: QuerySelectExpr) {
        return this.queryDescriptorSvc.getFormatter(expr.Expr as QueryExpr)?.id;
    }
    public getExprType(expr: QuerySelectExpr) {
        return this.queryDescriptorSvc.getExprType(expr.Expr as QueryExpr);
    }
    public getNextFormatter(currentExpr: QuerySelectExpr, nextExpr: QuerySelectExpr, currentFormatter: string | undefined) {
        const currentType = this.getExprType(currentExpr);
        const nextType = this.getExprType(nextExpr);
        const isCurrFmtDefault = this.getDefaultFormatter(currentExpr) === currentFormatter;
        const isNoopFmt = (fmt?: string) => !!FormatService.instance.getFormatter(fmt ?? '')?.meta?.noop;
        if (currentType !== nextType || !currentFormatter || isNoopFmt(currentFormatter) || isCurrFmtDefault) {
            return this.getDefaultFormatter(nextExpr);
        }
        return currentFormatter as NamedFormats;
    }

    public getChartSettings<TSettings>(defaultOverrides?: Partial<TSettings>) {
        const defaults = this.getDefaultSettings(this.chartType) ?? {};
        const settings = { ...defaults, ...defaultOverrides, ...this.settings.settings! };
        const observableSettings = makeAutoObservable(toJS(settings));
        deepObserve(observableSettings, () => this.applyChartSettings(settings));
        this.boundSettings = observableSettings;
        return settings as TSettings;
    }

    public applyChartSettings<TSettings>(settings: TSettings) {
        this.settings.settings = toJS(settings)!;
        this.onSettingsChanged.emit();
    }

    private invalidate() {
        this.onSettingsChanged.emit();
    }

    private getDefaultSettings(type: ChartTypes) {
        const typeInfo = chartInfo.get(type);
        return typeInfo?.getDefaults(this.getDatasource()!, this.schemaCtx, this.settings.groups, this.settings.values);
    }

    private createGetterSetter<T extends string>(name: T, array: Array<QuerySelectExpr>, index: number) {
        type TResult = { [K in T]: QuerySelectExpr } & { [K in T as `set${Capitalize<T>}`]: (expr: QuerySelectExpr) => void } & {
            [K in T as `remove${Capitalize<T>}`]: () => void;
        };
        const capitalized = name.slice(0, 1).toUpperCase() + name.slice(1);
        return {
            get [name]() {
                return array[index];
            },
            [`set${capitalized}`]: (expr: QuerySelectExpr) => {
                if (expr?.Expr) {
                    array[index] = expr;
                }
                this.invalidate();
            },
            [`remove${capitalized}`]: () => {
                array.splice(index, 1);
                this.invalidate();
            },
        } as TResult;
    }
}

export interface IChartEditor extends ChartEditorBase<IChartItemModel> {}

export class StandaloneChartEditor extends ChartEditorBase<IChartItemModel> implements IChartEditor {
    private constructor(public chartModel: IChartItemModel, public readonly schemaCtx: DatasourceSchemaContext, isEditMode = false) {
        super(chartModel, schemaCtx, isEditMode, false);
    }

    public static create(chartModel: IChartItemModel, schemaCtx: DatasourceSchemaContext) {
        return new StandaloneChartEditor(chartModel, schemaCtx, true);
    }
}

export class DashboardChartEditor extends ChartEditorBase<DashboardItemModel<ChartConfig>> implements IChartEditor {
    public constructor(public chartModel: DashboardItemModel<ChartConfig>, public readonly schemaCtx: DatasourceSchemaContext, isEditMode = false) {
        super(chartModel, schemaCtx, isEditMode);
    }

    public saveChanges() {
        super.saveChanges();
        this.chartModel.saveLayout();
    }
}
